import { DateType } from "@date-io/type";
import { TextField, TextFieldProps } from "@mui/material";
import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import { Controller, FieldValues, Path, useFormContext, Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

type FormDateTimePickerProps<IFormValues extends FieldValues = FieldValues> = {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    label?: string | JSX.Element,
    textFieldProps?: Omit<TextFieldProps, 'name' | 'value' | 'error'>
} & Omit<DateTimePickerProps<string, DateType>, 'renderInput' | 'value' | 'ref' | 'label' | 'onChange'> & {
    onChange?: DateTimePickerProps<string, DateType>['onChange']
}

const FormDateTimePicker = <IFormValues extends FieldValues = FieldValues>({
    name,
    control,
    label,
    textFieldProps,
    ...props
}: FormDateTimePickerProps<any>) => {
    const {t} = useTranslation(['expiredManagement']);
    const formContext = useFormContext<IFormValues>();

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            render={({ field, fieldState, formState }) =>
                <DateTimePicker
                    {...props}
                    ref={field.ref}
                    value={field.value}
                    onChange={(value) => {
                        field.onChange(value);
                        props.onChange?.(value);
                    }}
                    label={label}
                    inputFormat={props.inputFormat ?? 'DD/MM/YYYY HH:mm'}
                    ampm={props.ampm ?? false}
                    disabled={props.disabled ?? formState.isSubmitting}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...textFieldProps}
                            onBlur={field.onBlur}
                            error={Boolean(fieldState.error)}
                            helperText={t(!!fieldState.error?.message ? fieldState.error.message : '') ?? textFieldProps?.helperText}
                            fullWidth
                        />
                    }
                />
            }
        />
    )
};

export default FormDateTimePicker;